import React, { useState, useEffect, useRef } from 'react';
import {
  ComposableBlock,
  InterventionRecipe,
  SomaticBlockConfig,
  CognitiveBlockConfig,
  FrictionBlockConfig,
  SupportBlockConfig,
} from '../sync/recipe-types';

export interface InteractiveBreakSimulatorProps {
  recipe: InterventionRecipe;
  onCompleteSession?: (sessionSummary: {
    recipeId: string;
    completedAt: string;
    stepsCompleted: number;
    reflectionText?: string;
    cravingRating?: number;
  }) => void;
  onClose?: () => void;
}

export const InteractiveBreakSimulator: React.FC<InteractiveBreakSimulatorProps> = ({
  recipe,
  onCompleteSession,
  onClose,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [testBypassEnabled, setTestBypassEnabled] = useState<boolean>(false);

  // Somatic Step State
  const [somaticCycle, setSomaticCycle] = useState<number>(1);
  const [somaticPhase, setSomaticPhase] = useState<'Inhale' | 'Hold' | 'Exhale' | 'HoldAfter'>('Inhale');
  const [somaticTimer, setSomaticTimer] = useState<number>(4);
  const [somaticCompleted, setSomaticCompleted] = useState<boolean>(false);
  const [isSomaticActive, setIsSomaticActive] = useState<boolean>(true);

  // Cognitive Step State
  const [reflectionInput, setReflectionInput] = useState<string>('');
  const [cravingRating, setCravingRating] = useState<number>(6);

  // Friction Step State
  const [frictionRemaining, setFrictionRemaining] = useState<number>(60);
  const [isFrictionRunning, setIsFrictionRunning] = useState<boolean>(true);
  const [pledgeAccepted, setPledgeAccepted] = useState<boolean>(false);

  // Support Step State
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [audioPlayhead, setAudioPlayhead] = useState<number>(0);
  const [supportAcknowledged, setSupportAcknowledged] = useState<boolean>(false);

  const currentBlock: ComposableBlock | undefined = recipe.blocks[currentStepIndex];

  // Initialize or reset step state when currentStepIndex changes or recipe changes
  useEffect(() => {
    if (!currentBlock) return;

    if (currentBlock.type === 'somatic' && currentBlock.somaticConfig) {
      setSomaticCycle(1);
      setSomaticPhase('Inhale');
      setSomaticTimer(currentBlock.somaticConfig.inhaleSeconds || 4);
      setSomaticCompleted(false);
      setIsSomaticActive(true);
    } else if (currentBlock.type === 'friction' && currentBlock.frictionConfig) {
      setFrictionRemaining(currentBlock.frictionConfig.lockDurationSeconds || 45);
      setIsFrictionRunning(true);
      setPledgeAccepted(false);
    } else if (currentBlock.type === 'support') {
      setIsPlayingAudio(false);
      setAudioPlayhead(0);
      setSupportAcknowledged(false);
    }
  }, [currentStepIndex, currentBlock?.id]);

  // Somatic Timer Engine
  useEffect(() => {
    if (!currentBlock || currentBlock.type !== 'somatic' || !currentBlock.somaticConfig) return;
    if (!isSomaticActive || somaticCompleted) return;

    const config = currentBlock.somaticConfig;
    const totalCycles = config.cycles || 4;

    const interval = setInterval(() => {
      setSomaticTimer((prev) => {
        if (prev > 1) {
          return prev - 1;
        }

        // Phase Transition Logic
        if (somaticPhase === 'Inhale') {
          if ((config.holdSeconds || 0) > 0) {
            setSomaticPhase('Hold');
            return config.holdSeconds;
          } else {
            setSomaticPhase('Exhale');
            return config.exhaleSeconds;
          }
        } else if (somaticPhase === 'Hold') {
          setSomaticPhase('Exhale');
          return config.exhaleSeconds;
        } else if (somaticPhase === 'Exhale') {
          if ((config.holdAfterExhaleSeconds || 0) > 0) {
            setSomaticPhase('HoldAfter');
            return config.holdAfterExhaleSeconds || 4;
          } else {
            // End of this cycle
            if (somaticCycle >= totalCycles) {
              setSomaticCompleted(true);
              setIsSomaticActive(false);
              return 0;
            } else {
              setSomaticCycle((c) => c + 1);
              setSomaticPhase('Inhale');
              return config.inhaleSeconds;
            }
          }
        } else {
          // HoldAfter -> Next Cycle
          if (somaticCycle >= totalCycles) {
            setSomaticCompleted(true);
            setIsSomaticActive(false);
            return 0;
          } else {
            setSomaticCycle((c) => c + 1);
            setSomaticPhase('Inhale');
            return config.inhaleSeconds;
          }
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentBlock, isSomaticActive, somaticCompleted, somaticPhase, somaticCycle]);

  // Friction Timer Engine
  useEffect(() => {
    if (!currentBlock || currentBlock.type !== 'friction' || !currentBlock.frictionConfig) return;
    if (!isFrictionRunning || frictionRemaining <= 0) return;

    const interval = setInterval(() => {
      setFrictionRemaining((prev) => {
        if (prev <= 1) {
          setIsFrictionRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentBlock, isFrictionRunning, frictionRemaining]);

  // Audio Playhead Engine
  useEffect(() => {
    if (!isPlayingAudio) return;
    const interval = setInterval(() => {
      setAudioPlayhead((prev) => {
        if (prev >= 45) {
          setIsPlayingAudio(false);
          setSupportAcknowledged(true);
          return 45;
        }
        return prev + 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlayingAudio]);

  // Anti-Bypass Gate Evaluation
  const isCurrentStepUnlocked = (): boolean => {
    if (testBypassEnabled) return true;
    if (!currentBlock) return false;

    switch (currentBlock.type) {
      case 'somatic':
        return somaticCompleted;
      case 'cognitive':
        return reflectionInput.trim().length >= 5;
      case 'friction':
        return frictionRemaining === 0 && pledgeAccepted;
      case 'support':
        return supportAcknowledged;
      default:
        return true;
    }
  };

  const getAntiBypassLockMessage = (): string => {
    if (!currentBlock) return '';
    switch (currentBlock.type) {
      case 'somatic':
        return somaticCompleted
          ? '✓ Breath cycles completed'
          : `🔒 Anti-Bypass Gate: Complete all ${currentBlock.somaticConfig?.cycles || 4} breath cycles to unlock.`;
      case 'cognitive':
        return reflectionInput.trim().length >= 5
          ? '✓ Grounded reflection entered'
          : `🔒 Anti-Bypass Gate: Type a grounded reflection (min 5 chars, current: ${reflectionInput.trim().length}).`;
      case 'friction':
        if (frictionRemaining > 0) {
          return `🔒 Anti-Bypass Gate: Cooling-off countdown active (${frictionRemaining}s remaining).`;
        }
        return pledgeAccepted
          ? '✓ Values commitment affirmed'
          : '🔒 Anti-Bypass Gate: Check the pledge commitment box below to unlock.';
      case 'support':
        return supportAcknowledged
          ? '✓ Support acknowledged'
          : '🔒 Anti-Bypass Gate: Acknowledge or listen to grounding support to continue.';
      default:
        return '';
    }
  };

  // Next / Advance Handler
  const handleAdvanceStep = () => {
    if (!isCurrentStepUnlocked()) return;

    if (currentStepIndex < recipe.blocks.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      setIsCompleted(true);
      onCompleteSession?.({
        recipeId: recipe.id,
        completedAt: new Date().toISOString(),
        stepsCompleted: recipe.blocks.length,
        reflectionText: reflectionInput,
        cravingRating,
      });
    }
  };

  // Previous Step Handler
  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  // Reset Session
  const handleRestart = () => {
    setCurrentStepIndex(0);
    setIsCompleted(false);
    setSomaticCycle(1);
    setSomaticPhase('Inhale');
    setSomaticCompleted(false);
    setReflectionInput('');
    setPledgeAccepted(false);
    setSupportAcknowledged(false);
    setAudioPlayhead(0);
    setIsPlayingAudio(false);
  };

  // Completed Session Screen
  if (isCompleted) {
    return (
      <div
        className="interactive-break-simulator-completed"
        style={{
          backgroundColor: '#0F172A',
          borderRadius: '20px',
          padding: '32px 24px',
          color: '#FFFFFF',
          textAlign: 'center',
          boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
          border: '2px solid #10B981',
        }}
      >
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🌱</div>
        <h3 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: 800, color: '#10B981' }}>
          Micro-Session Completed!
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#94A3B8' }}>
          Your nervous system has successfully down-regulated. Impulse cooling-off audit envelope saved to your local encrypted journal.
        </p>

        <div
          style={{
            backgroundColor: '#1E293B',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'left',
            marginBottom: '24px',
            fontSize: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            color: '#CBD5E1',
          }}
        >
          <div>
            <strong style={{ color: '#F1F5F9' }}>Recipe:</strong> {recipe.name}
          </div>
          <div>
            <strong style={{ color: '#F1F5F9' }}>Intervention Steps Executed:</strong> {recipe.blocks.length} blocks
          </div>
          {reflectionInput && (
            <div>
              <strong style={{ color: '#F1F5F9' }}>Reflection Logged:</strong> "{reflectionInput}"
            </div>
          )}
          <div>
            <strong style={{ color: '#F1F5F9' }}>Privacy Guarantee:</strong> AES-256-GCM local encrypted record • Zero plaintext egress.
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={handleRestart}
            style={{
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ↺ Practice Session Again
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#334155',
                color: '#F1F5F9',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close Simulator
            </button>
          )}
        </div>
      </div>
    );
  }

  const unlocked = isCurrentStepUnlocked();
  const isFinalStep = currentStepIndex >= recipe.blocks.length - 1;

  return (
    <div
      className="interactive-break-simulator-root"
      style={{
        backgroundColor: '#0F172A',
        borderRadius: '20px',
        padding: '24px',
        color: '#FFFFFF',
        boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
        border: '2px solid #1E293B',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '480px',
        justifyContent: 'space-between',
      }}
    >
      {/* Simulator Header */}
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            borderBottom: '1px solid #1E293B',
            paddingBottom: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#10B981',
                display: 'inline-block',
                boxShadow: '0 0 8px #10B981',
              }}
            />
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              Real-Time Break Simulator
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* QA Testing Fast-Forward / Bypass Toggle */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '11px',
                color: '#94A3B8',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={testBypassEnabled}
                onChange={(e) => setTestBypassEnabled(e.target.checked)}
                style={{ accentColor: '#10B981' }}
              />
              QA Bypass Lock
            </label>

            <span style={{ fontSize: '11px', backgroundColor: '#1E293B', padding: '4px 10px', borderRadius: '6px', color: '#CBD5E1', fontWeight: 600 }}>
              Step {currentStepIndex + 1} of {recipe.blocks.length}
            </span>
          </div>
        </div>

        {/* Current Step Content */}
        {currentBlock ? (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 800, color: '#F8FAFC' }}>
                {currentBlock.title}
              </h4>
              <p style={{ margin: 0, fontSize: '12.5px', color: '#94A3B8' }}>
                {currentBlock.instructions}
              </p>
            </div>

            {/* SOMATIC PACER SIMULATION */}
            {currentBlock.type === 'somatic' && currentBlock.somaticConfig && (
              <div style={{ textAlign: 'center', margin: '24px 0' }}>
                {/* Visual Breathing Mandala Circle */}
                <div
                  className="somatic-mandala-circle"
                  style={{
                    width: '130px',
                    height: '130px',
                    borderRadius: '50%',
                    margin: '0 auto 16px',
                    backgroundColor:
                      somaticPhase === 'Inhale'
                        ? '#3B82F6'
                        : somaticPhase === 'Hold' || somaticPhase === 'HoldAfter'
                        ? '#F59E0B'
                        : '#10B981',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow:
                      somaticPhase === 'Inhale'
                        ? '0 0 32px rgba(59, 130, 246, 0.4)'
                        : somaticPhase === 'Exhale'
                        ? '0 0 32px rgba(16, 185, 129, 0.4)'
                        : '0 0 32px rgba(245, 158, 11, 0.4)',
                    transition: 'transform 0.9s ease, background-color 0.5s ease',
                    transform:
                      somaticPhase === 'Inhale'
                        ? 'scale(1.25)'
                        : somaticPhase === 'Hold' || somaticPhase === 'HoldAfter'
                        ? 'scale(1.2)'
                        : 'scale(0.85)',
                  }}
                >
                  <span style={{ fontSize: '12px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {somaticPhase === 'HoldAfter' ? 'Hold' : somaticPhase}
                  </span>
                  <span style={{ fontSize: '24px', fontWeight: 900, fontFamily: 'monospace' }}>
                    00:0{somaticTimer}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#CBD5E1', fontWeight: 600 }}>
                    Cycle {somaticCycle} of {currentBlock.somaticConfig.cycles}
                  </span>
                  {somaticCompleted && (
                    <span style={{ color: '#10B981', fontSize: '12px', fontWeight: 700 }}>
                      ✓ Completed
                    </span>
                  )}
                </div>

                {/* Progress Dots */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                  {Array.from({ length: currentBlock.somaticConfig.cycles }).map((_, idx) => (
                    <div
                      key={idx}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor:
                          idx + 1 < somaticCycle || somaticCompleted
                            ? '#10B981'
                            : idx + 1 === somaticCycle
                            ? '#3B82F6'
                            : '#334155',
                      }}
                    />
                  ))}
                </div>

                {!somaticCompleted && (
                  <div style={{ marginTop: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setSomaticCompleted(true)}
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #334155',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        fontSize: '10.5px',
                        color: '#64748B',
                        cursor: 'pointer',
                      }}
                    >
                      Fast-Forward Somatic Cycle
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* COGNITIVE CBT SIMULATION */}
            {currentBlock.type === 'cognitive' && currentBlock.cognitiveConfig && (
              <div
                style={{
                  backgroundColor: '#1E293B',
                  borderRadius: '14px',
                  padding: '16px',
                  border: '1px solid #334155',
                  margin: '16px 0',
                }}
              >
                <div style={{ fontSize: '13px', color: '#F1F5F9', fontWeight: 600, marginBottom: '10px' }}>
                  "{currentBlock.cognitiveConfig.promptQuestion}"
                </div>

                {currentBlock.cognitiveConfig.distortionTags && currentBlock.cognitiveConfig.distortionTags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                    {currentBlock.cognitiveConfig.distortionTags.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          fontSize: '10.5px',
                          backgroundColor: '#0F172A',
                          color: '#A78BFA',
                          border: '1px solid #6D28D9',
                          padding: '2px 8px',
                          borderRadius: '10px',
                        }}
                      >
                        🏷️ {tag}
                      </span>
                    ))}
                  </div>
                )}

                {currentBlock.cognitiveConfig.variant === 'craving_vs_need' && (
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94A3B8', marginBottom: '4px' }}>
                      <span>Craving Intensity: {cravingRating}/10</span>
                      <span style={{ color: cravingRating <= 3 ? '#10B981' : cravingRating <= 7 ? '#F59E0B' : '#EF4444' }}>
                        {cravingRating <= 3 ? 'Mild Urge' : cravingRating <= 7 ? 'Moderate Craving' : 'Acute Surge'}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={cravingRating}
                      onChange={(e) => setCravingRating(Number(e.target.value))}
                      style={{ width: '100%', accentColor: '#10B981' }}
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="interactive-cbt-reflection" style={{ fontSize: '11px', color: '#94A3B8', display: 'block', marginBottom: '4px' }}>
                    Grounded Reflection Response (required to unlock):
                  </label>
                  <textarea
                    id="interactive-cbt-reflection"
                    rows={3}
                    placeholder="Describe what you are noticing in your body and mind right now..."
                    value={reflectionInput}
                    onChange={(e) => setReflectionInput(e.target.value)}
                    style={{
                      width: '100%',
                      backgroundColor: '#0F172A',
                      color: '#F8FAFC',
                      border: reflectionInput.trim().length >= 5 ? '1px solid #10B981' : '1px solid #475569',
                      borderRadius: '8px',
                      padding: '8px 10px',
                      fontSize: '12px',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '10.5px', color: reflectionInput.trim().length >= 5 ? '#10B981' : '#94A3B8', marginTop: '2px' }}>
                    {reflectionInput.trim().length >= 5 ? '✓ Meets minimum requirement' : `${reflectionInput.trim().length}/5 characters`}
                  </div>
                </div>
              </div>
            )}

            {/* FRICTION INTENTIONAL DELAY SIMULATION */}
            {currentBlock.type === 'friction' && currentBlock.frictionConfig && (
              <div
                style={{
                  backgroundColor: '#1E293B',
                  borderRadius: '14px',
                  padding: '20px 16px',
                  border: '1px solid #334155',
                  textAlign: 'center',
                  margin: '16px 0',
                }}
              >
                <div
                  style={{
                    fontSize: '32px',
                    fontWeight: 900,
                    color: frictionRemaining > 0 ? '#F59E0B' : '#10B981',
                    fontFamily: 'monospace',
                    marginBottom: '8px',
                  }}
                >
                  00:{frictionRemaining < 10 ? `0${frictionRemaining}` : frictionRemaining}
                </div>

                <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#CBD5E1', fontStyle: 'italic' }}>
                  "{currentBlock.frictionConfig.pledgeText || 'I pause with intention before reacting.'}"
                </p>

                {currentBlock.frictionConfig.coreValues && currentBlock.frictionConfig.coreValues.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px', marginBottom: '16px' }}>
                    {currentBlock.frictionConfig.coreValues.map((val) => (
                      <span
                        key={val}
                        style={{
                          fontSize: '10.5px',
                          backgroundColor: '#0F172A',
                          color: '#FDE68A',
                          border: '1px solid #D97706',
                          padding: '2px 8px',
                          borderRadius: '10px',
                        }}
                      >
                        ⭐ {val}
                      </span>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    id="pledge-commitment-checkbox"
                    checked={pledgeAccepted}
                    onChange={(e) => setPledgeAccepted(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#10B981', cursor: 'pointer' }}
                  />
                  <label
                    htmlFor="pledge-commitment-checkbox"
                    style={{ fontSize: '12px', color: '#F1F5F9', fontWeight: 600, cursor: 'pointer' }}
                  >
                    I uphold this cooling-off contract and honor my values
                  </label>
                </div>

                {frictionRemaining > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setFrictionRemaining(0)}
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #334155',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        fontSize: '10.5px',
                        color: '#64748B',
                        cursor: 'pointer',
                      }}
                    >
                      Fast-Forward Friction Delay
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* SUPPORT AUDIO / 988 LIFELINE SIMULATION */}
            {currentBlock.type === 'support' && currentBlock.supportConfig && (
              <div
                style={{
                  backgroundColor: '#1E293B',
                  borderRadius: '14px',
                  padding: '16px',
                  border: '1px solid #334155',
                  margin: '16px 0',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#F1F5F9' }}>
                      {currentBlock.supportConfig.mediaTitle || 'Grounding Voice Note'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>
                      0:{audioPlayhead < 10 ? `0${audioPlayhead}` : audioPlayhead} / 0:45
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setIsPlayingAudio(!isPlayingAudio);
                      if (!isPlayingAudio) setSupportAcknowledged(true);
                    }}
                    style={{
                      backgroundColor: isPlayingAudio ? '#EF4444' : '#10B981',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {isPlayingAudio ? '⏸ Pause Audio' : '▶ Play Memo'}
                  </button>
                </div>

                <div
                  style={{
                    backgroundColor: '#0F172A',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    fontSize: '11.5px',
                    color: '#94A3B8',
                    fontStyle: 'italic',
                    marginBottom: '12px',
                    border: '1px solid #334155',
                  }}
                >
                  "{currentBlock.supportConfig.transcript || 'Take a slow, grounding breath. This moment will pass safely.'}"
                </div>

                {currentBlock.supportConfig.variant === 'crisis_988' && (
                  <div
                    style={{
                      backgroundColor: '#7F1D1D',
                      borderRadius: '10px',
                      padding: '12px',
                      textAlign: 'center',
                      marginBottom: '12px',
                      border: '1px solid #DC2626',
                    }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#FECACA', marginBottom: '4px' }}>
                      National 988 Suicide & Crisis Lifeline
                    </div>
                    <div style={{ fontSize: '11px', color: '#FCA5A5', marginBottom: '8px' }}>
                      Free, confidential, 24/7 mental health crisis support.
                    </div>
                    <a
                      href="tel:988"
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#DC2626',
                        color: '#FFFFFF',
                        textDecoration: 'none',
                        padding: '6px 16px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      📞 Tap to Call or Text 988
                    </a>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setSupportAcknowledged(true)}
                    style={{
                      backgroundColor: supportAcknowledged ? '#065F46' : '#1E293B',
                      color: supportAcknowledged ? '#A7F3D0' : '#F1F5F9',
                      border: supportAcknowledged ? '1px solid #10B981' : '1px solid #64748B',
                      borderRadius: '6px',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {supportAcknowledged ? '✓ Support Guidance Acknowledged' : 'Acknowledge Grounding Message'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Simulator Bottom Controls & Anti-Bypass Banner */}
      <div>
        {/* Anti-Bypass Status Indicator */}
        <div
          role="status"
          style={{
            backgroundColor: unlocked ? '#064E3B' : '#451A03',
            color: unlocked ? '#6EE7B7' : '#FCD34D',
            border: unlocked ? '1px solid #059669' : '1px solid #D97706',
            borderRadius: '8px',
            padding: '8px 12px',
            fontSize: '11.5px',
            fontWeight: 600,
            textAlign: 'center',
            marginBottom: '12px',
          }}
        >
          {getAntiBypassLockMessage()}
        </div>

        {/* Step Indicator Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '12px' }}>
          {recipe.blocks.map((_, idx) => (
            <div
              key={idx}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: currentStepIndex === idx ? '#10B981' : '#334155',
                transition: 'background-color 0.2s ease',
              }}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            disabled={currentStepIndex === 0}
            onClick={handlePreviousStep}
            style={{
              flex: 1,
              backgroundColor: currentStepIndex === 0 ? '#1E293B' : '#334155',
              color: currentStepIndex === 0 ? '#64748B' : '#F8FAFC',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: currentStepIndex === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            ← Previous
          </button>

          <button
            type="button"
            disabled={!unlocked}
            onClick={handleAdvanceStep}
            aria-label={isFinalStep ? 'Complete Micro-Session' : 'Next Step'}
            style={{
              flex: 2,
              backgroundColor: !unlocked ? '#1E293B' : '#10B981',
              color: !unlocked ? '#64748B' : '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              padding: '12px',
              fontSize: '13px',
              fontWeight: 800,
              cursor: !unlocked ? 'not-allowed' : 'pointer',
              boxShadow: unlocked ? '0 4px 14px rgba(16,185,129,0.3)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            {isFinalStep ? '✓ Complete Micro-Session' : 'Next Step →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteractiveBreakSimulator;
